import { useEffect, useMemo, useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Cross,
  Expand,
  MapPin,
  Navigation,
  Phone,
  Pill,
  RefreshCw,
  Stethoscope,
  WifiOff,
} from 'lucide-react';

import { SosGuide } from '@/components/care/SosGuide';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  FALLBACK_CENTER,
  FALLBACK_LABEL,
  directionsUrl,
  facilityStatus,
  formatDistance,
  haversineKm,
  lookupUrl,
  type Coords,
  type Facility,
  type FacilityType,
} from '@/lib/care';
import { fetchNearbyFacilities } from '@/lib/overpass';

const TYPE_META: Record<FacilityType, { label: string; icon: typeof Pill; classes: string }> = {
  pharmacy: { label: 'Pharmacy', icon: Pill, classes: 'bg-blush text-blush-foreground' },
  hospital: { label: 'Hospital', icon: Cross, classes: 'bg-aqua text-aqua-foreground' },
  clinic: { label: 'Clinic', icon: Stethoscope, classes: 'bg-lavender text-lavender-foreground' },
};

const STATUS_DOT: Record<string, string> = {
  open: 'bg-emerald-500',
  closed: 'bg-rose-400',
  unknown: 'bg-slate-300',
};

const STATUS_TEXT: Record<string, string> = {
  open: 'text-emerald-700',
  closed: 'text-rose-600',
  unknown: 'text-muted-foreground',
};

const RADIUS_STEPS = [4_000, 10_000, 25_000] as const;

type LocationState = 'locating' | 'live' | 'fallback';

/** Track the user's live position with the browser Geolocation API. */
function useLiveLocation() {
  const [coords, setCoords] = useState<Coords | null>(null);
  const [state, setState] = useState<LocationState>('locating');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!('geolocation' in navigator)) {
      setState('fallback');
      return;
    }
    setState('locating');
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        setState('live');
      },
      () => setState('fallback'),
      { enableHighAccuracy: true, maximumAge: 10_000, timeout: 20_000 },
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, [attempt]);

  return { coords, state, retry: () => setAttempt((a) => a + 1) };
}

function FacilityCard({ facility }: { facility: Facility }) {
  const meta = TYPE_META[facility.type];
  const status = facilityStatus(facility);
  const Icon = meta.icon;

  return (
    <Card className="rounded-3xl shadow-sm">
      <CardContent className="space-y-3.5 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <span
              className={cn(
                'flex size-11 shrink-0 items-center justify-center rounded-2xl',
                meta.classes,
              )}
            >
              <Icon className="size-5" aria-hidden />
            </span>
            <div>
              <h3 className="text-lg font-bold leading-snug">{facility.name}</h3>
              {facility.address && (
                <p className="text-sm text-muted-foreground">{facility.address}</p>
              )}
            </div>
          </div>
          <span className={cn('shrink-0 rounded-full px-3 py-1 text-xs font-bold', meta.classes)}>
            {meta.label}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm font-medium">
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <MapPin className="size-4" aria-hidden />
            {formatDistance(facility.distanceKm)} away
          </span>
          <span className="flex items-center gap-1.5">
            <span
              className={cn('size-2.5 rounded-full', STATUS_DOT[status.state])}
              aria-hidden
            />
            <span className={STATUS_TEXT[status.state]}>{status.label}</span>
          </span>
          {facility.hasEmergency && (
            <span className="rounded-full bg-blush px-2.5 py-0.5 text-xs font-bold text-blush-foreground">
              24h emergency
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          {facility.phone ? (
            <Button asChild className="h-12 rounded-2xl text-base font-bold">
              <a href={`tel:${facility.phone}`} aria-label={`Call ${facility.name}`}>
                <Phone className="size-5" aria-hidden />
                Call now
              </a>
            </Button>
          ) : (
            <Button
              asChild
              variant="outline"
              className="h-12 rounded-2xl border-blush-strong/40 bg-blush/50 text-base font-bold text-blush-foreground hover:bg-blush"
            >
              <a
                href={lookupUrl(facility)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Look up phone number for ${facility.name}`}
              >
                <Phone className="size-5" aria-hidden />
                Find number
              </a>
            </Button>
          )}
          <Button
            asChild
            variant="outline"
            className="h-12 rounded-2xl border-aqua-strong/40 bg-aqua/50 text-base font-bold text-aqua-foreground hover:bg-aqua"
          >
            <a
              href={directionsUrl(facility)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Directions to ${facility.name}`}
            >
              <Navigation className="size-5" aria-hidden />
              Directions
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function FacilitySkeleton() {
  return (
    <Card className="rounded-3xl shadow-sm">
      <CardContent className="space-y-3.5 p-5">
        <div className="flex items-center gap-3">
          <Skeleton className="size-11 rounded-2xl" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-5 w-2/3" />
            <Skeleton className="h-3.5 w-1/3" />
          </div>
        </div>
        <Skeleton className="h-4 w-1/2" />
        <div className="grid grid-cols-2 gap-2">
          <Skeleton className="h-12 rounded-2xl" />
          <Skeleton className="h-12 rounded-2xl" />
        </div>
      </CardContent>
    </Card>
  );
}

export default function CarePage() {
  useSeoMeta({
    title: 'Better Me — Emergency Care & Pharmacy Finder',
    description:
      'Find real nearby pharmacies and hospitals using your live location, with one-tap calling.',
  });

  const { coords, state: locationState, retry } = useLiveLocation();
  const [radiusIndex, setRadiusIndex] = useState(0);
  // Deep-linkable: /care?sos=1 opens straight into the SOS guide.
  const [searchParams] = useSearchParams();
  const [sosOpen, setSosOpen] = useState(searchParams.get('sos') === '1');
  const radius = RADIUS_STEPS[radiusIndex];

  // Where the user actually is right now (falls back to Nairobi CBD).
  const center = coords ?? FALLBACK_CENTER;

  // Only refetch when the user has meaningfully moved (>300 m) or the radius changes.
  const [queryCenter, setQueryCenter] = useState<Coords>(FALLBACK_CENTER);
  useEffect(() => {
    if (coords && haversineKm(coords, queryCenter) > 0.3) {
      setQueryCenter(coords);
    }
  }, [coords, queryCenter]);

  const {
    data: facilities,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['care-facilities', queryCenter.lat.toFixed(3), queryCenter.lon.toFixed(3), radius],
    queryFn: () => fetchNearbyFacilities(queryCenter, radius),
    enabled: locationState !== 'locating',
    staleTime: 5 * 60_000,
  });

  // If a tight search comes up empty, widen automatically one step.
  useEffect(() => {
    if (!isLoading && !isError && facilities && facilities.length === 0 && radiusIndex < RADIUS_STEPS.length - 1) {
      setRadiusIndex((i) => i + 1);
    }
  }, [isLoading, isError, facilities, radiusIndex]);

  const counts = useMemo(() => {
    const tally = { pharmacy: 0, hospital: 0, clinic: 0 };
    for (const f of facilities ?? []) tally[f.type] += 1;
    return tally;
  }, [facilities]);

  const showSkeletons = locationState === 'locating' || (isLoading && !facilities);
  const canWiden = radiusIndex < RADIUS_STEPS.length - 1;

  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Care &amp; pharmacies</h1>
        <p className="text-base text-muted-foreground">
          Real places near you, from OpenStreetMap — updated as you move.
        </p>
      </div>

      {/* Big SOS toggle — always visible at the top */}
      <button
        type="button"
        onClick={() => setSosOpen((v) => !v)}
        aria-expanded={sosOpen}
        className="w-full rounded-2xl bg-rose-600 py-4 text-center text-base font-black text-white shadow-lg transition-all hover:bg-rose-600/90 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-600 focus-visible:ring-offset-2"
      >
        {sosOpen
          ? '← Hide SOS guide & show care finder'
          : '🚨 In severe pain right now? Tap for the instant SOS guide'}
      </button>

      {sosOpen ? (
        <SosGuide />
      ) : (
        <>
      {/* Location status banner */}
      {locationState === 'locating' && (
        <div className="flex items-center gap-3 rounded-2xl border border-border bg-white px-4 py-3 text-sm font-medium text-muted-foreground">
          <RefreshCw className="size-4 animate-spin" aria-hidden />
          Finding your live location…
        </div>
      )}
      {locationState === 'live' && (
        <div className="flex items-center gap-3 rounded-2xl border border-aqua-strong/30 bg-aqua/50 px-4 py-3">
          <span className="relative flex size-2.5" aria-hidden>
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-aqua-strong opacity-60" />
            <span className="relative inline-flex size-2.5 rounded-full bg-aqua-strong" />
          </span>
          <p className="text-sm font-semibold text-aqua-foreground">
            Live location active — places update as you move
          </p>
        </div>
      )}
      {locationState === 'fallback' && (
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-lavender-strong/30 bg-lavender/60 px-4 py-3">
          <p className="text-sm font-semibold text-lavender-foreground">
            Location unavailable — showing real places near {FALLBACK_LABEL}
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={retry}
            className="h-9 shrink-0 rounded-xl text-lavender-foreground hover:bg-lavender"
          >
            <RefreshCw className="size-4" aria-hidden />
            Try again
          </Button>
        </div>
      )}

      {/* Results summary */}
      {facilities && facilities.length > 0 && (
        <p className="text-sm font-medium text-muted-foreground">
          {facilities.length} places within {Math.round(radius / 1000)} km
          {counts.pharmacy > 0 && <> · {counts.pharmacy} pharmacies</>}
          {counts.hospital > 0 && <> · {counts.hospital} hospitals</>}
          {counts.clinic > 0 && <> · {counts.clinic} clinics</>}
          {isFetching && <RefreshCw className="ml-2 inline size-3.5 animate-spin" aria-hidden />}
        </p>
      )}

      {/* Facility list */}
      <div className="space-y-4">
        {showSkeletons ? (
          <>
            <FacilitySkeleton />
            <FacilitySkeleton />
            <FacilitySkeleton />
          </>
        ) : isError ? (
          <Card className="rounded-3xl border-dashed shadow-none">
            <CardContent className="flex flex-col items-center gap-3 px-6 py-10 text-center">
              <WifiOff className="size-8 text-muted-foreground" aria-hidden />
              <p className="max-w-sm text-base text-muted-foreground">
                Couldn&apos;t load places from OpenStreetMap. Check your connection and try
                again.
              </p>
              <Button onClick={() => refetch()} className="h-12 rounded-2xl px-6 font-bold">
                <RefreshCw className="size-4" aria-hidden />
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : facilities && facilities.length > 0 ? (
          <>
            {facilities.map((facility) => (
              <FacilityCard key={facility.id} facility={facility} />
            ))}
            {canWiden && (
              <Button
                variant="outline"
                onClick={() => setRadiusIndex((i) => i + 1)}
                className="h-12 w-full rounded-2xl border-lavender-strong/40 bg-lavender/50 text-base font-bold text-lavender-foreground hover:bg-lavender"
              >
                <Expand className="size-5" aria-hidden />
                Search wider ({Math.round(RADIUS_STEPS[radiusIndex + 1] / 1000)} km)
              </Button>
            )}
          </>
        ) : (
          <Card className="rounded-3xl border-dashed shadow-none">
            <CardContent className="px-6 py-10 text-center">
              <p className="mx-auto max-w-sm text-base text-muted-foreground">
                No pharmacies or hospitals found within {Math.round(radius / 1000)} km of you.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      <p className="text-center text-xs leading-relaxed text-muted-foreground">
        Place data © OpenStreetMap contributors. Always call ahead to confirm stock and hours.
      </p>
        </>
      )}
    </div>
  );
}
